import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

// "Active" = last_active within this window. Generous enough that two people
// using the app a few minutes apart still see each other.
const WINDOW_MS = 10 * 60 * 1000   // 10 minutes
const BEAT_MS   = 120 * 1000      // refresh own last_active every 2m (well inside the 10m window)
const POLL_MS   = 60 * 1000       // re-check who's active every 60s

// Sends a heartbeat for the current user (keeps profiles.last_active fresh) and
// returns OTHER users active within the last 5 minutes, shaped for PresenceWidget:
//   [{ user_id, username, display_name, avatar_url }]
// `poll` gates the READ side only: the list is rendered for admins alone, so everyone
// else keeps the heartbeat (it feeds presence and last_active stats) and skips the
// once-a-minute profiles SELECT that nobody displayed.
export function useActiveUsers(user, { poll = true } = {}) {
  const [active, setActive] = useState([])

  useEffect(() => {
    const uid = user?.id
    if (!uid) { setActive([]); return }
    let alive = true

    const beat = () =>
      supabase.from('profiles')
        .update({ last_active: new Date().toISOString() })
        .eq('id', uid)
        .then(() => {}, () => {})

    const fetchActive = async () => {
      if (!poll) return
      const cutoff = new Date(Date.now() - WINDOW_MS).toISOString()
      const { data } = await supabase
        .from('profiles')
        .select('id, username, display_name, avatar_url, last_active')
        .gt('last_active', cutoff)
        .neq('id', uid)
        .order('last_active', { ascending: false })
        .limit(20)
      if (alive && data) {
        const next = data.map(p => ({
          user_id:      p.id,
          username:     p.username,
          display_name: p.display_name,
          avatar_url:   p.avatar_url,
        }))
        // Skip identical updates: this state lives at the App root, so a fresh
        // array every 45s re-rendered the ENTIRE mounted tree even when nobody
        // came or went (2026-06-10 perf audit). Compare by id signature.
        setActive(prev => {
          const sig = (arr) => arr.map(u => u.user_id).join(',')
          return sig(prev) === sig(next) ? prev : next
        })
      }
    }

    // Only beat/poll while the tab is actually visible — a backgrounded tab kept
    // hammering profiles with writes+reads (at scale, hundreds of hidden tabs each
    // doing ~1 write + 1 read/min added up to real QPS on a hot table for nothing).
    // The visibilitychange handler catches users back up the moment they return.
    const visible = () => typeof document === 'undefined' || document.visibilityState === 'visible'
    beat(); fetchActive()
    const beatIv = setInterval(() => { if (visible()) beat() }, BEAT_MS)
    const pollIv = setInterval(() => { if (visible()) fetchActive() }, POLL_MS)
    const onVis  = () => { if (document.visibilityState === 'visible') { beat(); fetchActive() } }
    document.addEventListener('visibilitychange', onVis)

    return () => {
      alive = false
      clearInterval(beatIv)
      clearInterval(pollIv)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [user?.id])

  return active
}
